import { ApiError, NetworkError, AuthError, ServerError } from './errors.js';
import { composeAbortSignals } from './compose-signals.js';

/**
 * @typedef {Object} UploadClientOptions
 * @property {string} baseUrl
 * @property {() => Promise<string | null>} getToken
 * @property {string} [endpoint] - Default upload endpoint path. Defaults to '/upload'.
 * @property {RequestCredentials} [credentials] - Fetch credentials option. If omitted, not included in the fetch init (fetch uses its own default).
 * @property {typeof globalThis.fetch} [fetchImpl]
 */

/**
 * Creates an upload client for file/multipart operations.
 *
 * Uses fetch + FormData. Does NOT set Content-Type — the browser auto-sets
 * multipart/form-data with the correct boundary.
 *
 * `opts.onProgress` is accepted by the contract but silently ignored in this
 * fetch-based implementation. A future XHR-based version can honor it.
 *
 * @param {UploadClientOptions} options
 * @returns {import('../../contracts/upload.js').UploadClient}
 */
export function createUploadClient({ baseUrl, getToken, endpoint = '/upload', credentials, fetchImpl }) {
  const doFetch = fetchImpl ?? globalThis.fetch.bind(globalThis);
  /** @type {AbortController} */
  let abortController = new AbortController();

  /**
   * @param {File} file
   * @param {import('../../contracts/upload.js').UploadOptions} [opts]
   * @returns {Promise<import('../../contracts/upload.js').UploadResult>}
   */
  async function upload(file, opts) {
    if (!(file instanceof Blob)) {
      throw new TypeError('upload() expects a File or Blob, got ' + typeof file);
    }

    const fullUrl = `${baseUrl}${opts?.endpoint ?? endpoint}`;

    const formData = new FormData();
    formData.append('file', file);

    if (opts?.metadata) {
      for (const [key, value] of Object.entries(opts.metadata)) {
        formData.append(key, value);
      }
    }

    /** @type {Record<string, string>} */
    const headers = {};
    const token = await getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const composedSignal = composeAbortSignals(abortController.signal, opts?.signal);

    /** @type {RequestInit} */
    const init = {
      method: 'POST',
      headers,
      body: formData,
      signal: composedSignal,
    };
    if (credentials !== undefined) {
      init.credentials = credentials;
    }

    /** @type {Response} */
    let response;
    try {
      response = await doFetch(fullUrl, init);
    } catch (err) {
      if (err && /** @type {*} */ (err).name === 'AbortError') {
        throw err;
      }
      throw new NetworkError(
        err instanceof Error ? err.message : 'Upload request failed',
      );
    }

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const text = (await response.text()).trim();
    if (!text) {
      throw new ApiError('Empty upload response', response.status);
    }

    /** @type {*} */
    let json;
    try {
      json = JSON.parse(text);
    } catch {
      throw new ApiError('Invalid JSON in upload response', response.status);
    }

    if (!json || typeof json.url !== 'string') {
      throw new ApiError('Upload response missing url', response.status);
    }

    return /** @type {import('../../contracts/upload.js').UploadResult} */ (json);
  }

  /**
   * @param {Response} response
   * @returns {Promise<never>}
   */
  async function handleErrorResponse(response) {
    /** @type {Record<string, unknown> | null} */
    let body = null;
    try {
      const raw = await response.text();
      body = /** @type {Record<string, unknown>} */ (JSON.parse(raw));
    } catch {
      // Ignore parse errors on error responses.
    }

    const message = (body && typeof body['message'] === 'string')
      ? body['message']
      : `HTTP ${response.status}`;

    if (response.status === 401 || response.status === 403) {
      throw new AuthError(message, response.status);
    }
    if (response.status >= 500) {
      throw new ServerError(message, response.status);
    }
    throw new ApiError(message, response.status);
  }

  return {
    upload,
    abort: () => {
      abortController.abort();
      abortController = new AbortController();
    },
  };
}
